import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeftIcon, ArrowUpRightIcon } from "lucide-react";
import { useMemo, useState } from "react";
import { z } from "zod";

import { specToStudySession } from "@/components/studyos/spec-to-session";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from "@/components/ui/resizable";
import { Skeleton } from "@/components/ui/skeleton";
import { versionAttemptsListQueryOptions, versionQueryOptions } from "@/hooks/query/thread";
import { useSession } from "@/lib/auth-client";
import { noIndexSeo } from "@/lib/seo";
import * as m from "@/paraglide/messages";
import { $deleteVersionAttempt } from "@/server/actions/content";

import { AttemptDetail } from "../-components/attempt/attempt-record-detail";
import { AttemptList } from "../-components/attempt/attempt-record-list";
import type { AttemptRecord } from "../-components/attempt/attempt-record-types";

export const Route = createFileRoute("/learn/$versionId/records")({
	component: RecordsPage,
	validateSearch: z.object({
		attemptId: z.string().optional(),
	}),
	head: ({ match }) => {
		return noIndexSeo({
			title: m.learn_recordsPageTitle(),
			description: m.learn_recordsDescription(),
			pathname: match.pathname,
		});
	},
});

function RecordsPage() {
	const { versionId } = Route.useParams();
	const { attemptId } = Route.useSearch();
	const navigate = Route.useNavigate();

	const [deleteAttemptId, setDeleteAttemptId] = useState<string | null>(null);

	const { data: session } = useSession();
	const role = session?.user?.role;
	const isAdmin = role === "admin" || role === "superadmin";

	const access = useSuspenseQuery(versionQueryOptions({ versionId })).data;

	const { data: attempts, isPending } = useQuery(
		versionAttemptsListQueryOptions({ versionId, enabled: true }),
	);

	const queryClient = useQueryClient();
	const deleteMutation = useMutation({
		mutationKey: ["version-attempts", "delete"],
		mutationFn: (nextAttemptId: string) =>
			$deleteVersionAttempt({ data: { attemptId: nextAttemptId } }),
		meta: {
			success: { message: m.learn_attemptDeleted() },
			error: { message: m.learn_attemptDeleteFailed() },
		},
		onSuccess: () => {
			// Remove the cached active attempt so navigating back doesn't
			// initialise the Engine with stale snapshot data.
			queryClient.removeQueries({
				queryKey: ["version-attempts", versionId],
			});
			setDeleteAttemptId(null);
		},
	});

	const typedAttempts = (attempts ?? []) as AttemptRecord[];
	const attempt = typedAttempts.find((entry) => entry.id === attemptId);

	const title = access.share?.title ?? m.learn_untitledSession();
	const threadId = access.status === "ok" ? access.version?.threadId : null;
	const showAttemptUsers =
		access.status === "ok" &&
		(access.version?.visibility === "public" || access.version?.visibility === "organization");

	// Convert version output to session so we can access full block definitions
	const sessionBlocks = useMemo(() => {
		const output = access.status === "ok" ? access.version?.output : null;
		if (output?.type !== "ui" || !output.ui) return undefined;
		return specToStudySession(output.ui).blocks;
	}, [access]);

	const attemptsList = (
		<AttemptList
			attempts={typedAttempts}
			attemptId={attemptId}
			isAdmin={isAdmin}
			showAttemptUsers={showAttemptUsers}
			onSelectAttempt={(nextAttemptId) => navigate({ search: { attemptId: nextAttemptId } })}
			onDeleteAttempt={setDeleteAttemptId}
		/>
	);

	const attemptDetail = attempt ? (
		<AttemptDetail
			attempt={attempt}
			showUser={showAttemptUsers}
			sessionBlocks={sessionBlocks}
			versionId={versionId}
		/>
	) : (
		<div className="py-12 text-center text-sm text-muted-foreground">
			{m.learn_selectAttempt()}
		</div>
	);

	return (
		<div className="flex h-full flex-col">
			<div className="shrink-0 border-b bg-background px-6 pt-6 pb-4">
				<div className="mx-auto flex w-full items-center gap-3 sm:max-w-4xl xl:max-w-7xl">
					<Button
						variant="ghost"
						size="icon"
						nativeButton={false}
						render={<Link to="/learn/$versionId" params={{ versionId }} />}
					>
						<ArrowLeftIcon className="size-4" />
					</Button>
					<div className="min-w-0 flex-1">
						<h1 className="truncate text-lg font-semibold">{title}</h1>
						<p className="text-sm text-muted-foreground">{m.learn_attemptRecords()}</p>
					</div>
					{threadId ? (
						<Button
							variant="outline"
							size="sm"
							nativeButton={false}
							render={<Link to="/chat/$threadId" params={{ threadId }} />}
						>
							{m.learn_openThread()}
							<ArrowUpRightIcon className="size-4" />
						</Button>
					) : null}
				</div>
			</div>

			<div className="flex-1 overflow-y-auto bg-muted/20 p-6">
				<div className="mx-auto w-full sm:max-w-4xl xl:max-w-7xl">
					{isPending ? (
						<div className="space-y-3">
							<Skeleton className="h-20 rounded-lg" />
							<Skeleton className="h-20 rounded-lg" />
							<Skeleton className="h-20 rounded-lg" />
						</div>
					) : typedAttempts.length === 0 ? (
						<div className="py-12 text-center text-sm text-muted-foreground">
							{m.learn_noAttemptsRecorded()}
						</div>
					) : (
						<>
							<div className="space-y-6 lg:hidden">
								{attemptsList}
								{attemptDetail}
							</div>

							<div className="hidden h-full min-h-[640px] lg:block">
								<ResizablePanelGroup
									direction="horizontal"
									className="h-full items-stretch"
								>
									<ResizablePanel
										defaultSize={"20%"}
										minSize={"10%"}
										maxSize={"40%"}
										className="pr-3"
									>
										<div className="h-full overflow-y-auto">{attemptsList}</div>
									</ResizablePanel>
									<ResizableHandle withHandle />
									<ResizablePanel
										defaultSize="80%"
										minSize="50%"
										className="pl-3"
									>
										<div className="h-full overflow-y-auto">
											{attemptDetail}
										</div>
									</ResizablePanel>
								</ResizablePanelGroup>
							</div>
						</>
					)}
				</div>
			</div>

			<AlertDialog
				open={!!deleteAttemptId}
				onOpenChange={(open) => {
					if (!open) setDeleteAttemptId(null);
				}}
			>
				<AlertDialogContent size="sm">
					<AlertDialogHeader>
						<AlertDialogTitle>{m.learn_deleteAttemptTitle()}</AlertDialogTitle>
						<AlertDialogDescription>
							{m.learn_deleteAttemptDescription()}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>{m.common_cancel()}</AlertDialogCancel>
						<AlertDialogAction
							variant="destructive"
							onClick={() => {
								if (deleteAttemptId) {
									deleteMutation.mutate(deleteAttemptId);
								}
							}}
						>
							{m.common_delete()}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</div>
	);
}
